import { useCallback, useMemo, useState } from 'react';

import dlv from 'dlv';

import { GetMeDocument, useSetLoyaltyRewardsTierMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAuthContext } from 'state/auth';
import { NUM_RECENT_PURCHASED_ITEMS } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { ILoyaltyCategory } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { IUseSelectedReward } from './types';
import { useLoyaltyCategories } from './use-loyalty-categories';

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

const formatLoyaltyCategories = (loyaltyCategories: ILoyaltyCategory[]) =>
  loyaltyCategories.reduce((acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
    options.forEach(val => (acc[val._id] = val));
    return acc;
  }, {});

export const useSelectedReward = (): IUseSelectedReward => {
  const { user } = useAuthContext();
  const shouldUniqueByModifiers = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS);
  const { logEvent } = useMParticleContext();
  const logger = useLogger();
  const { activeCard } = useLoyaltyActiveCard();
  const loyaltyCategories = useLoyaltyCategories();
  const [setSelectedRewardsTier] = useSetLoyaltyRewardsTierMutation();

  const { cardId = '' } = activeCard || {};

  const selectedRewardTierId = useMemo(() => dlv(user || {}, 'details.loyaltyTier') ?? null, [
    user,
  ]);

  const [updatingRewardTier, setUpdatingRewardTier] = useState(false);
  const [errorUpdatingRewardTier, setErrorUpdatingRewardTier] = useState(false);

  const logRewardTierChangedEvent = useCallback(
    (rewardTierId: string, success: boolean, message?: string) => {
      logEvent(CustomEventNames.REWARD_TIER_CHANGED, EventTypes.UserPreference, {
        'Reward Tier ID': rewardTierId,
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
      });
    },
    [logEvent]
  );

  const onChangeRewardTier = useCallback(
    async (newLoyaltyTierId: string | null) => {
      if (!newLoyaltyTierId || !cardId) {
        return;
      }

      setUpdatingRewardTier(true);

      try {
        await setSelectedRewardsTier({
          variables: {
            cardId,
            rewardsTier: newLoyaltyTierId,
          },
          refetchQueries: [
            {
              query: GetMeDocument,
              variables: {
                customInput: { shouldUniqueByModifiers },
                numUniquePurchasedItems: NUM_RECENT_PURCHASED_ITEMS,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
        logRewardTierChangedEvent(newLoyaltyTierId, true);
      } catch (error) {
        logger.error({ error });
        logRewardTierChangedEvent(newLoyaltyTierId, false, error);
        setErrorUpdatingRewardTier(true);
      } finally {
        setUpdatingRewardTier(false);
      }
    },
    [cardId, logRewardTierChangedEvent, logger, setSelectedRewardsTier, shouldUniqueByModifiers]
  );

  const dismissUpdateError = useCallback(() => {
    setErrorUpdatingRewardTier(false);
  }, []);

  const selectedRewardTier = useMemo(() => {
    if (loyaltyCategories) {
      const loyaltyCategoriesById = formatLoyaltyCategories(loyaltyCategories);
      return loyaltyCategoriesById[selectedRewardTierId ?? ''];
    }
    return undefined;
  }, [selectedRewardTierId, loyaltyCategories]);

  const selectedRewardCategoryId = useMemo(() => {
    if (loyaltyCategories && loyaltyCategories.length) {
      const selected = loyaltyCategories.find(({ options }) =>
        options.find(({ _id }) => _id === selectedRewardTierId)
      );
      return selected?._id;
    }
    return undefined;
  }, [loyaltyCategories, selectedRewardTierId]);

  return {
    dismissUpdateError,
    errorUpdatingRewardTier,
    selectedRewardCategoryId,
    selectedRewardTier,
    selectedRewardTierId,
    onChangeRewardTier,
    updatingRewardTier,
  };
};
